import { TERMINAL_EXTRACTION_STATUSES } from './ProcessReportModal.constants';
import {
  COMPANY_NAME_PLACEHOLDER,
  CONTACT_NAME_PLACEHOLDER,
  DASHBOARD_URL_PLACEHOLDER,
  FINAL_REPORT_DELIVERY_SECTION,
  REPORT_FILENAME_PLACEHOLDER,
} from './ProcessReportModal.templates';
import type { FinalReportEmailTemplate } from './ProcessReportModal.types';

// Best-effort recovery of the Valuation-relative date folder (Company/Date/) from a
// stored file key like `Valuation/Company/Date/Report/file.pdf` so the picker can
// reopen at the remembered folder.
export const deriveDatePrefix = (key: string | null): string => {
  if (!key) {
    return '';
  }
  const relative = key.startsWith('Valuation/') ? key.slice('Valuation/'.length) : key;
  const parts = relative.split('/').filter(Boolean);
  if (parts.length >= 3) {
    return `${parts.slice(0, parts.length - 2).join('/')}/`;
  }
  return '';
};

export const isTerminalExtractionStatus = (status: string | null | undefined): boolean =>
  Boolean(status) && TERMINAL_EXTRACTION_STATUSES.includes((status as string).toLowerCase());

export const toggleExclusivePath = (prev: string | null, path: string): string | null => (prev === path ? null : path);

export const formatCombinedEmail = (subject: string, body: string): string => {
  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  if (trimmedSubject && trimmedBody) {
    return `Subject: ${trimmedSubject}\n\n${trimmedBody}`;
  }
  return trimmedSubject ? `Subject: ${trimmedSubject}` : trimmedBody;
};

// Inverse of formatCombinedEmail: split a stored draft into its editable
// Subject and body fields. Drafts without a subject line are treated as body.
export const parseCombinedEmail = (combined: string): { subject: string; body: string } => {
  const text = combined ?? '';
  const match = /^[ \t]*Subject:[ \t]*(.*)(?:\r?\n)?/.exec(text);
  if (!match) {
    return { subject: '', body: text.trim() };
  }
  return { subject: match[1].trim(), body: text.slice(match[0].length).trim() };
};

export const fileNameFromPath = (path: string | null): string =>
  path ? (path.split('/').filter(Boolean).at(-1) ?? '') : '';

const applyFinalReportPlaceholders = (
  text: string,
  {
    companyName,
    contactName,
    reportFilename,
    dashboardUrl,
  }: {
    companyName?: string | null;
    contactName?: string | null;
    reportFilename?: string | null;
    dashboardUrl?: string | null;
  }
): string =>
  text
    .replaceAll(COMPANY_NAME_PLACEHOLDER, companyName?.trim() || 'the company')
    .replaceAll(CONTACT_NAME_PLACEHOLDER, contactName?.trim() || 'there')
    .replaceAll(REPORT_FILENAME_PLACEHOLDER, reportFilename?.trim() || 'the final report')
    .replaceAll(DASHBOARD_URL_PLACEHOLDER, dashboardUrl?.trim() || '');

export const composeFinalReportEmail = ({
  template,
  companyName,
  contactName,
  reportFilename,
  dashboardUrl,
}: {
  template: FinalReportEmailTemplate;
  companyName?: string | null;
  contactName?: string | null;
  reportFilename?: string | null;
  dashboardUrl?: string | null;
}): string => {
  const placeholders = { companyName, contactName, reportFilename, dashboardUrl };
  const subject = applyFinalReportPlaceholders(template.emailSubject, placeholders);
  const introduction = applyFinalReportPlaceholders(template.introduction, placeholders).trim();
  const deliverySection = applyFinalReportPlaceholders(
    FINAL_REPORT_DELIVERY_SECTION,
    placeholders
  ).trim();
  const body = [introduction, deliverySection].filter(Boolean).join('\n\n');
  return formatCombinedEmail(subject, body);
};
